/**
 * Owner: Masa Bayu
 * File: qx-writer-session.ts
 * Date:Time: 2026-10-16 06:23
 * Description: Session tip · editorHtml · gap · templateId · images.
 */

import { qxwHasFixedPages, type QXWDocument } from '@qx/qxw-core';
import type { DraftFigureFloat, DraftFigurePicture } from './qx-draft-image';
import { parseDraftFigureFloat } from './qx-draft-image';
import {
  parseDraftFigureFrame,
  parseDraftFigureShadow,
} from './qx-draft-image-style';
import {
  idbDelete,
  idbGet,
  idbPut,
  isQXWriterIdbAvailable,
  STORE_DRAFT_SESSION,
} from './qx-idb';

const SESSION_KEY = 'qxwriter.draft.session.v1';
const SESSION_IDB_KEY = 'draft.session.v1';
const PREVIEWS_IDB_KEY = 'draft.previews.v1';
/** Full contentEditable HTML · includes img data-URLs (reload SoT for pictures). */
const EDITOR_HTML_IDB_KEY = 'draft.editorHtml.v1';

export type WriterSessionDraft = {
  readonly doc: QXWDocument;
  readonly orderedListBlockIndexes: readonly number[];
  readonly dropcapBlockIndexes: readonly number[];
  readonly dropcapFontsByBlockIndex: Readonly<Record<number, string>>;
  readonly previewByAssetId: Readonly<Record<string, string>>;
  readonly previewWidthByAssetId: Readonly<Record<string, number>>;
  readonly previewHeightByAssetId: Readonly<Record<string, number>>;
  readonly previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>>;
  readonly previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>>;
  /** contentEditable innerHTML snapshot · preferred paint path for images. */
  readonly editorHtml: string;
  /** Document-wide paragraph gap (pt) · restored on reload. */
  readonly paragraphGapPt: number;
  /** Catalog template id for select UI only · not QXW metadata. */
  readonly templateId: string;
  readonly savedAt: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringMap(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string' && raw.length > 0) out[key] = raw;
  }
  return out;
}

function numberMap(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  const out: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) out[key] = raw;
  }
  return out;
}

function floatMap(value: unknown): Record<string, DraftFigureFloat> {
  if (!isRecord(value)) return {};
  const out: Record<string, DraftFigureFloat> = {};
  for (const [key, raw] of Object.entries(value)) {
    const parsed = parseDraftFigureFloat(typeof raw === 'string' ? raw : null);
    if (parsed) out[key] = parsed;
  }
  return out;
}

function pictureMap(value: unknown): Record<string, DraftFigurePicture> {
  if (!isRecord(value)) return {};
  const out: Record<string, DraftFigurePicture> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (!isRecord(raw)) continue;
    const frame = parseDraftFigureFrame(typeof raw.frame === 'string' ? raw.frame : null);
    const shadow = parseDraftFigureShadow(typeof raw.shadow === 'string' ? raw.shadow : null);
    if (frame && shadow) out[key] = { frame, shadow };
  }
  return out;
}

function numberIndexes(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
}

function dropcapFontMap(value: unknown): Record<number, string> {
  if (!isRecord(value)) return {};
  const out: Record<number, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    const index = Number(key);
    if (!Number.isFinite(index) || typeof raw !== 'string' || !raw.trim()) continue;
    out[index] = raw.trim();
  }
  return out;
}

function parseSession(value: unknown): WriterSessionDraft | null {
  let record: unknown = value;
  if (typeof value === 'string') {
    try {
      record = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(record) || !isRecord(record.doc)) return null;
  const doc = record.doc as unknown as QXWDocument;
  const identity = isRecord((doc as { identity?: unknown }).identity)
    ? (doc as { identity: Record<string, unknown> }).identity
    : null;
  if (identity?.schema !== 'qxw' || !Array.isArray(doc.elements)) return null;
  // D4 · layout tetap ialah dokumen Studio (stor editor) · bukan autosaf mod Tulis.
  if (qxwHasFixedPages(doc)) return null;
  return {
    doc,
    orderedListBlockIndexes: numberIndexes(record.orderedListBlockIndexes),
    dropcapBlockIndexes: numberIndexes(record.dropcapBlockIndexes),
    dropcapFontsByBlockIndex: dropcapFontMap(record.dropcapFontsByBlockIndex),
    previewByAssetId: stringMap(record.previewByAssetId),
    previewWidthByAssetId: numberMap(record.previewWidthByAssetId),
    previewHeightByAssetId: numberMap(record.previewHeightByAssetId),
    previewFloatByAssetId: floatMap(record.previewFloatByAssetId),
    previewPictureByAssetId: pictureMap(record.previewPictureByAssetId),
    editorHtml: typeof record.editorHtml === 'string' ? record.editorHtml : '',
    paragraphGapPt:
      typeof record.paragraphGapPt === 'number' &&
      Number.isFinite(record.paragraphGapPt) &&
      record.paragraphGapPt >= 0
        ? Math.round(record.paragraphGapPt)
        : 0,
    templateId:
      typeof record.templateId === 'string' && record.templateId.trim().length > 0
        ? record.templateId.trim()
        : '',
    savedAt: typeof record.savedAt === 'number' ? record.savedAt : 0,
  };
}

function buildPayload(
  doc: QXWDocument,
  orderedListBlockIndexes: readonly number[],
  previewByAssetId: Readonly<Record<string, string>>,
  previewWidthByAssetId: Readonly<Record<string, number>>,
  previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>>,
  previewHeightByAssetId: Readonly<Record<string, number>>,
  dropcapBlockIndexes: readonly number[],
  previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>>,
  dropcapFontsByBlockIndex: Readonly<Record<number, string>>,
  editorHtml: string,
  paragraphGapPt: number = 0,
  templateId: string = '',
): WriterSessionDraft {
  return {
    doc,
    orderedListBlockIndexes: [...orderedListBlockIndexes],
    dropcapBlockIndexes: [...dropcapBlockIndexes],
    dropcapFontsByBlockIndex: { ...dropcapFontsByBlockIndex },
    previewByAssetId: { ...previewByAssetId },
    previewWidthByAssetId: { ...previewWidthByAssetId },
    previewHeightByAssetId: { ...previewHeightByAssetId },
    previewFloatByAssetId: { ...previewFloatByAssetId },
    previewPictureByAssetId: { ...previewPictureByAssetId },
    editorHtml,
    paragraphGapPt: Math.max(0, Math.round(paragraphGapPt)),
    templateId: templateId.trim(),
    savedAt: Date.now(),
  };
}

/**
 * sessionStorage cache.
 * With IDB: tip-only slim (images live in IDB).
 * Without IDB: full payload (tests / restricted browsers).
 */
function writeSessionStorage(payload: WriterSessionDraft): void {
  const slim: WriterSessionDraft = {
    ...payload,
    previewByAssetId: {},
    editorHtml: '',
  };
  const primary = isQXWriterIdbAvailable() ? slim : payload;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(primary));
  } catch {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(slim));
    } catch (err) {
      console.warn('[WriterSession] sessionStorage write skipped:', err);
    }
  }
}

function readSessionStorage(): WriterSessionDraft | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return parseSession(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

async function writeIdbDurable(payload: WriterSessionDraft): Promise<void> {
  if (!isQXWriterIdbAvailable()) return;
  try {
    // JSON string · reliable round-trip for large data-URLs
    await idbPut(STORE_DRAFT_SESSION, JSON.stringify(payload), SESSION_IDB_KEY);
    await idbPut(STORE_DRAFT_SESSION, payload.previewByAssetId, PREVIEWS_IDB_KEY);
    if (payload.editorHtml.includes('<img')) {
      await idbPut(STORE_DRAFT_SESSION, payload.editorHtml, EDITOR_HTML_IDB_KEY);
    }
  } catch (err) {
    console.warn('[WriterSession] IndexedDB write failed:', err);
  }
}

function mergeSessions(
  primary: WriterSessionDraft,
  secondary: WriterSessionDraft | null,
  previewExtra: Readonly<Record<string, string>>,
  editorHtmlExtra: string,
): WriterSessionDraft {
  const base =
    secondary && secondary.savedAt > primary.savedAt ? secondary : primary;
  const other = base === primary ? secondary : primary;
  const editorHtml =
    (base.editorHtml.includes('<img') && base.editorHtml) ||
    (other?.editorHtml.includes('<img') && other.editorHtml) ||
    (editorHtmlExtra.includes('<img') && editorHtmlExtra) ||
    base.editorHtml ||
    other?.editorHtml ||
    '';
  const gapPrimary = primary.paragraphGapPt;
  const gapSecondary = secondary?.paragraphGapPt ?? 0;
  const templateId =
    (base.templateId.trim() ||
      other?.templateId.trim() ||
      primary.templateId.trim() ||
      secondary?.templateId.trim() ||
      '');
  return {
    ...base,
    paragraphGapPt: Math.max(gapPrimary, gapSecondary, base.paragraphGapPt),
    templateId,
    editorHtml,
    previewByAssetId: {
      ...(other?.previewByAssetId ?? {}),
      ...base.previewByAssetId,
      ...previewExtra,
    },
    previewWidthByAssetId: {
      ...(other?.previewWidthByAssetId ?? {}),
      ...base.previewWidthByAssetId,
    },
    previewHeightByAssetId: {
      ...(other?.previewHeightByAssetId ?? {}),
      ...base.previewHeightByAssetId,
    },
    previewFloatByAssetId: {
      ...(other?.previewFloatByAssetId ?? {}),
      ...base.previewFloatByAssetId,
    },
    previewPictureByAssetId: {
      ...(other?.previewPictureByAssetId ?? {}),
      ...base.previewPictureByAssetId,
    },
  };
}

export async function writeWriterSessionDraft(
  doc: QXWDocument,
  orderedListBlockIndexes: readonly number[] = [],
  previewByAssetId: Readonly<Record<string, string>> = {},
  previewWidthByAssetId: Readonly<Record<string, number>> = {},
  previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>> = {},
  previewHeightByAssetId: Readonly<Record<string, number>> = {},
  dropcapBlockIndexes: readonly number[] = [],
  previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>> = {},
  dropcapFontsByBlockIndex: Readonly<Record<number, string>> = {},
  editorHtml: string = '',
  paragraphGapPt: number = 0,
  templateId: string = '',
): Promise<void> {
  // D4 · autosaf ini hanya untuk mod Tulis · SoT dokumen layout tetap ialah stor editor.
  if (qxwHasFixedPages(doc)) return;
  const payload = buildPayload(
    doc,
    orderedListBlockIndexes,
    previewByAssetId,
    previewWidthByAssetId,
    previewFloatByAssetId,
    previewHeightByAssetId,
    dropcapBlockIndexes,
    previewPictureByAssetId,
    dropcapFontsByBlockIndex,
    editorHtml,
    paragraphGapPt,
    templateId,
  );
  writeSessionStorage(payload);
  await writeIdbDurable(payload);
}

export async function readWriterSessionDraft(): Promise<WriterSessionDraft | null> {
  const fromSs = readSessionStorage();
  let fromIdb: WriterSessionDraft | null = null;
  let previewExtra: Record<string, string> = {};
  let editorHtmlExtra = '';
  if (isQXWriterIdbAvailable()) {
    try {
      fromIdb = parseSession(await idbGet<unknown>(STORE_DRAFT_SESSION, SESSION_IDB_KEY));
      previewExtra = stringMap(await idbGet<unknown>(STORE_DRAFT_SESSION, PREVIEWS_IDB_KEY));
      const html = await idbGet<unknown>(STORE_DRAFT_SESSION, EDITOR_HTML_IDB_KEY);
      if (typeof html === 'string') editorHtmlExtra = html;
    } catch (err) {
      console.warn('[WriterSession] IndexedDB read failed:', err);
    }
  }
  if (!fromIdb && !fromSs) return null;
  if (!fromIdb) {
    return fromSs
      ? {
          ...fromSs,
          previewByAssetId: { ...fromSs.previewByAssetId, ...previewExtra },
          editorHtml: fromSs.editorHtml || editorHtmlExtra,
        }
      : null;
  }
  if (!fromSs) {
    return {
      ...fromIdb,
      previewByAssetId: { ...fromIdb.previewByAssetId, ...previewExtra },
      editorHtml: fromIdb.editorHtml || editorHtmlExtra,
    };
  }
  return mergeSessions(fromIdb, fromSs, previewExtra, editorHtmlExtra);
}

export function clearWriterSessionDraft(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  if (isQXWriterIdbAvailable()) {
    void idbDelete(STORE_DRAFT_SESSION, SESSION_IDB_KEY).catch(() => {});
    void idbDelete(STORE_DRAFT_SESSION, PREVIEWS_IDB_KEY).catch(() => {});
    void idbDelete(STORE_DRAFT_SESSION, EDITOR_HTML_IDB_KEY).catch(() => {});
  }
}

type SessionPreviewRefs = {
  readonly previewByAssetIdRef: { current: Record<string, string> };
  readonly previewWidthByAssetIdRef: { current: Record<string, number> };
  readonly previewHeightByAssetIdRef: { current: Record<string, number> };
  readonly previewFloatByAssetIdRef: { current: Record<string, DraftFigureFloat> };
  readonly previewPictureByAssetIdRef: { current: Record<string, DraftFigurePicture> };
  readonly orderedListIndexesRef: { current: readonly number[] };
  readonly dropcapIndexesRef: { current: readonly number[] };
  readonly dropcapFontsRef: { current: Record<number, string> };
};

type SyncSnapshot = {
  readonly doc: QXWDocument;
  readonly orderedListBlockIndexes: readonly number[];
  readonly dropcapBlockIndexes: readonly number[];
  readonly dropcapFontsByBlockIndex: Readonly<Record<number, string>>;
  readonly previewByAssetId: Readonly<Record<string, string>>;
  readonly previewWidthByAssetId: Readonly<Record<string, number>>;
  readonly previewHeightByAssetId: Readonly<Record<string, number>>;
  readonly previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>>;
  readonly previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>>;
};

function applySyncToRefs(
  synced: SyncSnapshot,
  refs: SessionPreviewRefs,
  editorHtml: string,
  paragraphGapPt: number,
  templateId: string = '',
): WriterSessionDraft {
  refs.previewByAssetIdRef.current = {
    ...refs.previewByAssetIdRef.current,
    ...synced.previewByAssetId,
  };
  refs.previewWidthByAssetIdRef.current = {
    ...refs.previewWidthByAssetIdRef.current,
    ...synced.previewWidthByAssetId,
  };
  refs.previewHeightByAssetIdRef.current = {
    ...refs.previewHeightByAssetIdRef.current,
    ...synced.previewHeightByAssetId,
  };
  refs.previewFloatByAssetIdRef.current = {
    ...refs.previewFloatByAssetIdRef.current,
    ...synced.previewFloatByAssetId,
  };
  refs.previewPictureByAssetIdRef.current = {
    ...refs.previewPictureByAssetIdRef.current,
    ...synced.previewPictureByAssetId,
  };
  refs.orderedListIndexesRef.current = synced.orderedListBlockIndexes;
  refs.dropcapIndexesRef.current = synced.dropcapBlockIndexes;
  refs.dropcapFontsRef.current = { ...synced.dropcapFontsByBlockIndex };
  return buildPayload(
    synced.doc,
    synced.orderedListBlockIndexes,
    refs.previewByAssetIdRef.current,
    refs.previewWidthByAssetIdRef.current,
    refs.previewFloatByAssetIdRef.current,
    refs.previewHeightByAssetIdRef.current,
    synced.dropcapBlockIndexes,
    refs.previewPictureByAssetIdRef.current,
    refs.dropcapFontsRef.current,
    editorHtml,
    paragraphGapPt,
    templateId,
  );
}

export function persistWriterSessionFromSync(
  synced: SyncSnapshot,
  refs: SessionPreviewRefs,
  editorHtml: string = '',
  paragraphGapPt: number = 0,
  templateId: string = '',
): void {
  const payload = applySyncToRefs(synced, refs, editorHtml, paragraphGapPt, templateId);
  writeSessionStorage(payload);
  void writeIdbDurable(payload);
}

export async function persistWriterSessionFromSyncAwait(
  synced: SyncSnapshot,
  refs: SessionPreviewRefs,
  editorHtml: string = '',
  paragraphGapPt: number = 0,
  templateId: string = '',
): Promise<void> {
  const payload = applySyncToRefs(synced, refs, editorHtml, paragraphGapPt, templateId);
  writeSessionStorage(payload);
  await writeIdbDurable(payload);
}

/** Prefer raw editor HTML when it still carries image bytes. */
export function sessionPaintHtml(
  session: WriterSessionDraft,
  reconstructed: string,
): string {
  if (session.editorHtml.includes('<img')) return session.editorHtml;
  return reconstructed;
}
